import { Pipe, PipeTransform } from '@angular/core';
import { LocalizedDateFns } from '@freelancer/ui/localized-date-fns.service';
import { addMilliseconds } from 'date-fns';

export enum DurationFormat {
  LONG = 'long',
  SHORT = 'short',
}

/**
 * Converts milliseconds to a human readable duration.
 *
 * The output can be formatted in two ways, long and short format:
 *
 * Long Format:
 * ```
 * {{ '1000' | duration:'long' }} => '1 second'
 * {{ '62 * 60 * 1000 | duration:'long' }} => '1 hour, 2 minutes'
 * ```
 *
 * Short Format (default):
 * ```
 * {{ '1000' | duration:'short' }} => '00 : 00 : 01'
 * {{ '62 * 60 * 1000 | duration:'short' }} => '01 : 02 : 00'
 * ```
 */
@Pipe({ name: 'duration' })
export class DurationPipe implements PipeTransform {
  readonly SECS_IN_DAY = 86400;
  readonly SECS_IN_HOUR = 3600;
  readonly SECS_IN_MIN = 60;

  constructor(private dateFns: LocalizedDateFns) {}

  transform(millis: number | null): string | null;
  transform(
    millis: number | null,
    format: DurationFormat.LONG,
  ): Promise<string>;
  transform(
    millis: number | null,
    format: DurationFormat = DurationFormat.SHORT,
  ): Promise<string> | string | null {
    // safety guard for undefined when template is loading
    if (millis === null) {
      return null;
    }

    const seconds = millis / 1000;
    const days = Math.floor(seconds / this.SECS_IN_DAY);
    let hours = Math.floor(seconds / this.SECS_IN_HOUR);
    const mins = Math.floor((seconds % this.SECS_IN_HOUR) / this.SECS_IN_MIN);
    const secs = (seconds % this.SECS_IN_HOUR) % this.SECS_IN_MIN;

    if (format === DurationFormat.LONG) {
      // different format for hours
      hours = Math.floor((seconds % this.SECS_IN_DAY) / this.SECS_IN_HOUR);

      const daysInMillis = days * 86_400_000;
      const hoursInMillis = hours * 3_600_000;
      const minsInMillis = mins * 60000;
      const secsInMillis = secs * 1000;

      const formattedValue = Promise.all([
        this.calculateRelativeTime(daysInMillis),
        this.calculateRelativeTime(hoursInMillis),
        this.calculateRelativeTime(minsInMillis),
        this.calculateRelativeTime(secsInMillis),
      ]).then(formattedValues => {
        const start = formattedValues.findIndex(
          value => value.charAt(0) !== '0',
        );
        if (start !== -1) {
          return formattedValues
            .slice(start, start + 2)
            .filter(x => x.charAt(0) !== '0')
            .join(', ');
        }
        return '';
      });
      return formattedValue;
    }

    const hoursFormatted =
      hours.toString().length > 1 ? hours : `0${hours}`.slice(-2);
    const minsFormatted = `0${mins}`.slice(-2);
    const secsFormatted = `0${secs}`.slice(-2);

    return `${hoursFormatted} : ${minsFormatted} : ${secsFormatted}`;
  }

  private calculateRelativeTime(timeInMillis: number): Promise<string> {
    const now = new Date();
    return this.dateFns.formatDistanceStrict(
      addMilliseconds(now, timeInMillis),
      now,
    );
  }
}
